import { isFulfilled } from "@reduxjs/toolkit";
import { getIn, useFormik } from "formik";
import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY, DEFAULT_SEARCH_INPUT_LENGTH, NOTIFICATION_TIME_OUT } from "../../constants/app";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useSubmitOnEnter from "../../hooks/useSubmitOnEnter";
import { Centre, TEnquiryFormValues } from "../../models/enquiry";
import Roles from "../../models/roles";
import { AUTH_ROUTE, NON_AUTH_ROUTE } from "../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import { showNotificationAction } from "../../store/application/slice";
import { getAddressSuggestionsThunk } from "../../store/auth/thunks";
import { AddressSuggestion, TGetAddressSuggestionsResponse } from "../../store/auth/types";
import { createEnquiryThunk } from "../../store/enquiry/thunks";
import { createValidationSchema } from "../../utils/createValidationSchema";
import { capitalizeName } from "../../utils/stringUtils";
import CustomButton from "../UI/Buttons/CustomButton";
import RoundedPlus from "../UI/Icons/RoundedPlus";
import RoundedTick from "../UI/Icons/RoundedTick";
import TextArea from "../UI/Inputs/TextArea";
import TextInput from "../UI/Inputs/TextInput";
import ErrorMessage from "../UI/Tooltips/ErrorMessage";

const Enquiry: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement | null>(null); // Ref to the dropdown menu for detecting click outside

  const inProgress = useSelector((state) => state.enquiry.inProgress);
  const authInProgress = useSelector((state) => state.auth.inProgress);
  const error = useSelector((state) => state.enquiry.error);
  const user = useSelector((state) => state.auth.user);

  const isCentreAdmin = useMemo(
    () => user?.role.name === Roles.CENTER_ADMIN,
    [user]
  );

  const [centres, setCentres] = useState<Centre[]>([
    {
      business_name: "",
      website: "",
      center_phone: "",
      address: "",
    },
  ]);
  const [searchInput, setSearchInput] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [centreIndex, setCentreIndex] = useState(0);
  const [addressSuggestions, setAddressSuggestions] = useState<Record<number, AddressSuggestion[]>>({});
  const [openAddressSuggestionDropdown, setOpenAddressSuggestionDropdown] = useState<number | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenAddressSuggestionDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddBusiness = () => {
    setCentres([
      ...centres,
      {
        business_name: "",
        website: "",
        center_phone: "",
        address: "",
      },
    ]);
  };

  const removeCentre = (index: number) => {
    if (centres.length > 1) {
      setCentres(centres.filter((_, i) => i !== index));
    }
  };

  /**
   * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
   * The debounced value is used to trigger the API call for fetching candidates.
   */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
   * Handles the change in the search input by updating the `selectedKeyword` state.
   * It also resets the current page to 1 if the search input is empty or exceeds the default length.
   *
   */
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch)
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if(selectedKeyword !== ""){
      const handleAddressSuggestions = async(selectedKeyword: string) => {
        const response = await dispatch(getAddressSuggestionsThunk(selectedKeyword));

        if(isFulfilled(response)){
          setAddressSuggestions((prev) => ({
            ...prev,
            [centreIndex]: (response.payload as TGetAddressSuggestionsResponse)?.data,
          }));
        }
      };

      handleAddressSuggestions(selectedKeyword)
    } else {
      setAddressSuggestions((prev) => ({
        ...prev,
        [centreIndex]: [], // Clears suggestions only for the selected index
      }));
    }
  }, [dispatch, selectedKeyword, centreIndex])

  const handleNestedChange = async (
    index: number,
    field: string,
    value: string,
    hasSelectedAddress?: boolean
  ) => {
    const updatedCentres = [...centres];
    updatedCentres[index] = {
      ...updatedCentres[index],
      [field]: value,
    };
    setCentres(updatedCentres);

    // Fetch address suggestions only for the specific index
    if (field === "address" && !hasSelectedAddress) {
      setSearchInput(value)
      setCentreIndex(index)
      setOpenAddressSuggestionDropdown(index); // Open dropdown for this index
    }
  };


  /** Submit the form for enquiry-details */
  const { handleSubmit, values, errors, touched, setFieldValue, handleChange } =
    useFormik({
      validationSchema: isCentreAdmin
        ? createValidationSchema("centreAdminEnquiry")
        : createValidationSchema("enquiry"),
      validateOnBlur: false,
      validateOnChange: false,
      initialValues: {
        first_name: "",
        last_name: "",
        phone_number: "",
        email: "",
        comment: "",
        centre_information: [] as Centre[],
      },
      onSubmit: async (values: TEnquiryFormValues) => {
        const payload = {
          ...values,
          first_name: capitalizeName(values.first_name),
          last_name: capitalizeName(values.last_name),
          email: values.email.toLowerCase(),
        };
        try {
          const result = await dispatch(createEnquiryThunk(payload));
          if (isFulfilled(result)) {
            sessionStorage.clear();
            if (location.pathname.includes("client")) {
              dispatch(showNotificationAction("Thank you for your enquiry, the Connected Crew team will finish setting up your profile and be in touch."));
              setTimeout(() => {
                navigate(AUTH_ROUTE.CLIENT_CENTRES);
              } , NOTIFICATION_TIME_OUT);              
            } else {
              navigate(NON_AUTH_ROUTE.SUCCESS_ENQUIRY);
            }
          }
        } catch (error) {
          throw new Error(
            "Could not connect to services at the moment. Please try again. Thanks."
          );
        }
      },
    });

  useEffect(() => {
    if (centres.length > 0) {
      setFieldValue("centre_information", centres);
    }
  }, [centres, setFieldValue]);

  useSubmitOnEnter(handleSubmit);

  const handleSelectAddress = (index: number, selectedAddress: string) => {
    handleNestedChange(index, "address", selectedAddress, true);

    setAddressSuggestions((prev) => ({
      ...prev,
      [index]: [], // Clears suggestions only for the selected index
    }));

    setOpenAddressSuggestionDropdown(null);
    setSelectedKeyword("")
  };

  return (
    <>
      {!isCentreAdmin && (
        <div className="d-flex flex-column gap-20">
          <div className="fs-16 fw-700 text-black">Primary Contact</div>
          <div className="d-flex flex-column gap-10">
            <div className="d-flex flex-column gap-10">
              <div className="d-flex flex-column flex-lg-row gap-10">
                <div className="flex-fill">
                  <TextInput
                    value={values.first_name}
                    field={"first_name"}
                    label={"First Name"}
                    onChange={handleChange}
                    touched={touched.first_name! as boolean}
                    error={errors.first_name as string}
                    placeholder={" " + ""}
                  />
                </div>
                <div className="flex-fill">
                  <TextInput
                    value={values.last_name}
                    field={"last_name"}
                    label={"Last Name"}
                    onChange={handleChange}
                    touched={touched.last_name! as boolean}
                    error={errors.last_name as string}
                    placeholder={" " + ""}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex flex-column gap-10">
              <div className="d-flex flex-column flex-lg-row gap-10">
                <div className="w-180px mobile-w-100">
                  <TextInput
                    value={values.phone_number || ""}
                    field={"phone_number"}
                    label={"Phone Number"}
                    onChange={handleChange}
                    touched={touched.phone_number! as boolean}
                    error={errors.phone_number as string}
                    placeholder={" " + ""}
                  />
                </div>
                <div className="flex-fill">
                  <TextInput
                    value={values.email || ""}
                    field={"email"}
                    label={"Email Address"}
                    onChange={handleChange}
                    touched={touched.email! as boolean}
                    error={errors.email as string}
                    placeholder={" "}
                  />
                  {error && <ErrorMessage message={error?.errors?.email?.[0]} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {isCentreAdmin && (
        <div className="fs-30 fw-400">Register a new centre</div>
      )}
      <div className="d-flex flex-column gap-20">
        <div className="fs-16 fw-700 text-black">Centre Information</div>
        <div className="business-list d-flex flex-column gap-20">
          {centres.map((centre, index) => (
            <div
              key={index}
              className="business-list-item d-flex flex-column gap-20"
            >
              <div className="d-flex flex-column gap-10">
                <div className="d-flex flex-column gap-10">
                  <div className="d-flex flex-column flex-lg-row gap-10">
                    <div className="flex-fill">
                      <TextInput
                        value={centre.business_name}
                        field={`centre_information[${index}].business_name`}
                        label={"Business Name"}
                        onChange={(e) =>
                          handleNestedChange(
                            index,
                            "business_name",
                            e.target.value
                          )
                        }
                        touched={
                          !!getIn(
                            touched,
                            `centre_information[${index}].business_name`
                          )
                        }
                        error={
                          getIn(
                            errors,
                            `centre_information[${index}].business_name`
                          ) ?? ""
                        }
                        placeholder={" "}
                      />
                    </div>
                    <div className="flex-fill">
                      <TextInput
                        value={centre.website}
                        field={`centre_information[${index}].website`}
                        label={"Website"}
                        onChange={(e) => handleNestedChange(index, "website", e.target.value)}                        
                        touched={!!getIn(
                          touched,
                          `centre_information[${index}].website`
                        )}
                        error={
                          getIn(
                            errors,
                            `centre_information[${index}].website`
                          ) ?? ""
                        }
                        placeholder={" "}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-column gap-10">
                  <div className="d-flex flex-column flex-lg-row gap-10">
                    <div className="w-180px mobile-w-100">
                      <TextInput
                        value={centre.center_phone}
                        field={`centre_information[${index}].center_phone`}
                        label={"Phone Number"}
                        onChange={(e) =>
                          handleNestedChange(
                            index,
                            "center_phone",
                            e.target.value
                          )
                        }
                        touched={!!getIn(
                          touched,
                          `centre_information[${index}].center_phone`
                        )}
                        error={
                          getIn(
                            errors,
                            `centre_information[${index}].center_phone`
                          ) ?? ""
                        }
                        placeholder={" "}
                      />
                    </div>
                    <div className="flex-fill position-relative">
                      <TextInput
                        value={centre.address}
                        field={`centre_information[${index}].address`}
                        label={"Address"}
                        onChange={(e) => {
                          handleNestedChange(index, "address", e.target.value);
                        }}
                        touched={!!getIn(
                          touched,
                          `centre_information[${index}].address`
                        )}
                        error={
                          getIn(
                            errors,
                            `centre_information[${index}].address`
                          ) ?? ""
                        }
                        placeholder={" "}
                      />
                      {authInProgress ? (
                        ""
                      ) : searchInput.length >= DEFAULT_SEARCH_INPUT_LENGTH &&
                        openAddressSuggestionDropdown === index ? (
                        addressSuggestions[index]?.length > 0 ? (
                        <div ref={dropdownRef} className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
                          {addressSuggestions[index]?.map((addr, i) => (
                            <div
                              key={i}
                              className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                              onClick={() =>
                                handleSelectAddress(index, addr.description)
                              }
                            >
                              {addr.description}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div ref={dropdownRef} className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">
                          No Address Found
                        </div>
                        )
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {error && (
                <ErrorMessage message={String(error?.errors?.[`centre_information.${index}.business_name`]?.[0] ?? "")} />
              )}
              {centres.length > 1 && (
                <>
                  <div className="w-100 h-1px bg-grey-dark"></div>
                  <svg
                    className="min-w-24px action-remove"
                    onClick={() => removeCentre(index)}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.92898 19.0711C1.02373 15.1658 1.02373 8.83418 4.92898 4.92893C8.83422 1.02369 15.1659 1.02369 19.0711 4.92893C22.9764 8.83418 22.9764 15.1658 19.0711 19.0711C15.1659 22.9763 8.83422 22.9763 4.92898 19.0711ZM6.21463 6.21458C3.01943 9.40978 3.01943 14.5902 6.21463 17.7854C9.40983 20.9806 14.5903 20.9806 17.7855 17.7854C20.9807 14.5902 20.9807 9.40978 17.7855 6.21458C14.5903 3.01938 9.40983 3.01938 6.21463 6.21458ZM8.78592 8.78588C9.14095 8.43086 9.71655 8.43086 10.0716 8.78588L12 10.7144L13.9285 8.78588C14.2835 8.43086 14.8591 8.43086 15.2142 8.78588C15.5692 9.1409 15.5692 9.7165 15.2142 10.0715L13.2857 12L15.2142 13.9285C15.5692 14.2835 15.5692 14.8591 15.2142 15.2141C14.8591 15.5691 14.2835 15.5691 13.9285 15.2141L12 13.2856L10.0716 15.2141C9.71655 15.5691 9.14095 15.5691 8.78592 15.2141C8.4309 14.8591 8.4309 14.2835 8.78592 13.9285L10.7144 12L8.78592 10.0715C8.4309 9.71651 8.4309 9.1409 8.78592 8.78588Z"
                      fill="black"
                    />
                  </svg>
                </>
              )}
            </div>
          ))}
        </div>
        {/* <ErrorMessage message={errors.centre_information as string} /> */}
        <div className="d-flex justify-content-center">
          <CustomButton
            type="button"
            variant="primary"
            onClick={handleAddBusiness}
            className="btn-theme bg-white text-black"
          >
            <RoundedPlus fill="#000000" />
            Add Another Centre
          </CustomButton>
        </div>
      </div>
      <div className="d-flex flex-column gap-20">
        <div className="fs-16 fw-700 text-black">Comment (Optional)</div>
        <TextArea
          value={values.comment}
          field={"comment"}
          // label={"Add comment..."}
          placeholder={"Add comment..."}
          onChange={handleChange}
        />
        <div className="d-flex justify-content-end">
          <CustomButton
            type="submit"
            isLoading={inProgress}
            variant="primary"
            onClick={handleSubmit}
            contentClass={"d-flex"}
            className="btn-theme bg-teal text-black"
          >
            <RoundedTick color={inProgress ? "#aaaaaa" : "#000000"} />
            Submit Enquiry
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default memo(Enquiry);
